import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Space, Typography } from 'antd';

import { useQuery } from '@tanstack/react-query';

import { useSettingStore } from '../../store';
import { SUMMARY } from '../../utilities/functions';

import EquityCurveChart from './components/equity-chart';
import StatisticsPanel from './components/statistics-panel';
import { runMonteCarloSimulation } from './utils/monte-carlo';

import './styles.scss';

const { Title, Text } = Typography;

// Default values when no trade data is available
const DEFAULT_PARAMS = {
    startingEquity: 12500,
    winPercentage: 50,
    avgWinnerLoserRatio: 1.5,
    avgTradesPerMonth: 40,
    riskPerTrade: 125
};

const EquityCurvePage = () => {
    const { filter } = useSettingStore((state) => state);

    // Fetch defaults from backend
    const { isLoading, data: defaultsFromBackend } = useQuery(
        ['equity-curve-defaults', filter],
        () => SUMMARY.fetchEquityCurveDefaults(filter),
        {
            staleTime: 1000 * 60 * 60, // 1 hour
            cacheTime: 1000 * 60 * 60 * 2 // 2 hours
        }
    );

    // State for simulation parameters
    const [params, setParams] = useState(DEFAULT_PARAMS);
    const [originalDefaults, setOriginalDefaults] = useState(null);

    // Initialize params when defaults are loaded
    useEffect(() => {
        if (defaultsFromBackend) {
            const initialParams = {
                startingEquity: defaultsFromBackend.startingEquity || DEFAULT_PARAMS.startingEquity,
                winPercentage: defaultsFromBackend.winPercentage || DEFAULT_PARAMS.winPercentage,
                avgWinnerLoserRatio:
                    defaultsFromBackend.avgWinnerLoserRatio || DEFAULT_PARAMS.avgWinnerLoserRatio,
                avgTradesPerMonth:
                    defaultsFromBackend.avgTradesPerMonth || DEFAULT_PARAMS.avgTradesPerMonth,
                riskPerTrade: defaultsFromBackend.riskPerTrade || DEFAULT_PARAMS.riskPerTrade
            };

            setParams(initialParams);
            setOriginalDefaults(initialParams);
        } else if (!isLoading && !defaultsFromBackend) {
            // No data available, use default values
            setParams(DEFAULT_PARAMS);
            setOriginalDefaults(null);
        }
    }, [defaultsFromBackend, isLoading]);

    // Load params from session storage on mount
    useEffect(() => {
        const savedParams = sessionStorage.getItem('equityCurveParams');
        if (savedParams) {
            try {
                const parsed = JSON.parse(savedParams);
                setParams(parsed);
            } catch (error) {
                console.error('Error parsing saved params:', error);
            }
        }
    }, []);

    // Save params to session storage when changed
    useEffect(() => {
        if (params) {
            sessionStorage.setItem('equityCurveParams', JSON.stringify(params));
        }
    }, [params]);

    // Run simulation
    const scenarios = useMemo(() => {
        if (!params) return [];

        // Validate params
        if (
            params.startingEquity <= 0 ||
            params.winPercentage < 0 ||
            params.winPercentage > 100 ||
            params.avgWinnerLoserRatio <= 0 ||
            params.avgTradesPerMonth <= 0 ||
            params.riskPerTrade <= 0
        ) {
            return [];
        }

        return runMonteCarloSimulation(params, 20, 12);
    }, [params]);

    const handleParamChange = (key, value) => {
        setParams((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleReset = () => {
        if (originalDefaults) {
            setParams(originalDefaults);
            sessionStorage.setItem('equityCurveParams', JSON.stringify(originalDefaults));
        } else {
            setParams(DEFAULT_PARAMS);
            sessionStorage.setItem('equityCurveParams', JSON.stringify(DEFAULT_PARAMS));
        }
    };

    const hasDefaults = originalDefaults !== null;

    return (
        <div className="equity-curve-page">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Page Header */}
                <div className="page-header">
                    <Title level={3}>Equity Curve Simulator</Title>
                    <Text type="secondary">
                        Visualize your potential equity growth over the next 12 months based on your
                        trading statistics. Adjust the parameters to see different scenarios.
                    </Text>
                </div>

                {/* Main Content */}
                <Row gutter={[24, 24]}>
                    {/* Left Column - Statistics Panel */}
                    <Col xs={24} lg={8}>
                        <StatisticsPanel
                            params={params}
                            onParamChange={handleParamChange}
                            onReset={handleReset}
                            loading={isLoading}
                            hasDefaults={hasDefaults}
                        />
                    </Col>

                    {/* Right Column - Chart */}
                    <Col xs={24} lg={16}>
                        {scenarios.length > 0 ? (
                            <EquityCurveChart scenarios={scenarios} params={params} />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                <Text type="secondary">
                                    Adjust the parameters to generate simulation scenarios
                                </Text>
                            </div>
                        )}
                    </Col>
                </Row>
            </Space>
        </div>
    );
};

export default EquityCurvePage;
